
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import missingImageIcon from '../../assets/images/missing-image-icon.svg'

import { formatDaysToDeliveryDisplay } from '../../hooks/displayTools'
import { DeliveryCalendarPopover } from '../Product/DeliveryCalendarPopover/DeliveryCalendarPopover'

export interface Product {
  name: string
  id?: string
  image_url?: string
  moq?: string
  production_time?: string
  free_shipping?: string
  isCategory?: boolean
  url?: string
}

interface ProductsGridProps {
  products: Product[]
  columns?: 2 | 4
  onProductSelect: (name: string, url: string) => void
}

export const ProductsCarouselElement = ({
  products,
  columns = 2,
  onProductSelect,
}: ProductsGridProps): JSX.Element => {
  const [staticProducts] = useState(() => structuredClone(products ?? []))

  const handleProductSelect = (item: Product) => {
    onProductSelect(item.name, item.url ?? '')
  }

  return (
    <div className='my-4 flex w-full flex-col items-center justify-center'>
      <div className='w-full max-w-[750px] overflow-hidden rounded-2xl bg-white'>
        <div className={`grid gap-4 p-4 ${columns === 4 ? 'grid-cols-4' : 'grid-cols-2'}`}>
          {staticProducts.map((item, index) =>
            !item?.isCategory ? (
              <div
                key={item.url ?? index}
                className='flex cursor-pointer flex-col items-start justify-start rounded-2xl bg-white transition-transform duration-100 active:scale-95 active:opacity-80'
                onClick={() => handleProductSelect(item)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleProductSelect(item)
                }}
              >
                <div
                  className='relative w-full overflow-hidden rounded-[4px]'
                  style={{ aspectRatio: '5/6' }}
                >
                  <Image
                    src={item.image_url || missingImageIcon}
                    alt={item.name}
                    fill
                    className='object-cover'
                    unoptimized
                  />
                </div>
                <h4 className='Type-Large-Medium mt-2 text-left'>{item.name}</h4>
                <p className='text-[12px] text-[#757575]'>
                  Minimum: {Number(String(item.moq).replace(/,/g, '')).toLocaleString() || item.moq}
                </p>
                <div
                  className='flex items-center gap-1.5 justify-start'
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className='text-[12px] text-[#757575]'>
                    Est. Delivery: {formatDaysToDeliveryDisplay(item.production_time ?? '')}
                  </span>
                  <DeliveryCalendarPopover production_time={Number(item.production_time ?? 0)} />
                </div>
              </div>
            ) : (
              <Link
                key={item.url ?? index}
                href={item.url ?? ''}
                className='group relative w-full overflow-hidden rounded-[8px]'
                style={{ aspectRatio: '5/6', textDecoration: 'none', color: 'inherit' }}
              >
                {item?.image_url ? (
                  <div
                    className='absolute inset-0 bg-cover bg-center'
                    style={{ backgroundImage: `url(${item.image_url})` }}
                  />
                ) : (
                  <div
                    className='absolute inset-0 bg-cover bg-center'
                    style={{ backgroundImage: `url(${missingImageIcon?.src})` }}
                  />
                )}
                <div className='absolute inset-0 bg-black/40' />
                <div className='absolute inset-0 flex flex-col items-start justify-end px-3 pb-3 text-left'>
                  <h3 className='Type-Large-Medium text-pure-white no-underline!'>{item.name}</h3>
                </div>
              </Link>
            ),
          )}
        </div>
      </div>
    </div>
  )
}
